import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

/**
 * NeoGo custom theme preset — extends PrimeVue Aura
 * Professional palettes for both light and dark modes
 */
const NeoGoPreset = definePreset(Aura, {
  semantic: {
    // Brand primary — WhatsApp-inspired green
    primary: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#022c22',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617',
        },
        primary: {
          color: '#059669',
          contrastColor: '#ffffff',
          hoverColor: '#047857',
          activeColor: '#065f46',
        },
        highlight: {
          background: '#ecfdf5',
          focusBackground: '#d1fae5',
          color: '#047857',
          focusColor: '#065f46',
        },
        focusRing: {
          color: '{primary.color}',
          offset: '2px',
          width: '2px',
          style: 'solid',
        },
        formField: {
          hoverBorderColor: '{primary.color}',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f4f4f5',
          200: '#e4e4e7',
          300: '#d4d4d8',
          400: '#a1a1aa',
          500: '#71717a',
          600: '#52525b',
          700: '#3f3f46',
          800: '#27272a',
          900: '#18181b',
          950: '#09090b',
        },
        primary: {
          color: '#34d399',
          contrastColor: '#09090b',
          hoverColor: '#6ee7b7',
          activeColor: '#a7f3d0',
        },
        highlight: {
          background: 'rgba(52, 211, 153, 0.16)',
          focusBackground: 'rgba(52, 211, 153, 0.24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
        focusRing: {
          color: '{primary.color}',
          offset: '2px',
          width: '2px',
          style: 'solid',
        },
        // Force neutral form field colors — no green backgrounds
        formField: {
          background: '{surface.800}',
          disabledBackground: '{surface.700}',
          filledBackground: '{surface.800}',
          filledHoverBackground: '{surface.800}',
          filledFocusBackground: '{surface.800}',
          borderColor: '{surface.600}',
          hoverBorderColor: '{surface.500}',
          focusBorderColor: '{primary.color}',
          color: '{surface.0}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.400}',
          floatLabelColor: '{surface.400}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.400}',
          floatLabelInvalidColor: '#ef4444',
        },
      },
    },
  },
  // Component-level overrides for select/dropdown
  components: {
    select: {
      colorScheme: {
        dark: {
          root: {
            background: '{surface.800}',
            disabledBackground: '{surface.700}',
            filledBackground: '{surface.800}',
            filledHoverBackground: '{surface.800}',
            filledFocusBackground: '{surface.800}',
            borderColor: '{surface.600}',
            hoverBorderColor: '{surface.500}',
            focusBorderColor: '{primary.color}',
            color: '{surface.0}',
          },
          overlay: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
            color: '{surface.0}',
          },
          option: {
            focusBackground: '{surface.700}',
            selectedBackground: 'rgba(52, 211, 153, 0.16)',
            selectedFocusBackground: 'rgba(52, 211, 153, 0.24)',
            color: '{surface.0}',
            focusColor: '{surface.0}',
            selectedColor: '{surface.0}',
            selectedFocusColor: '{surface.0}',
          },
        },
      },
    },
    inputtext: {
      colorScheme: {
        dark: {
          root: {
            background: '{surface.800}',
            filledBackground: '{surface.800}',
            filledHoverBackground: '{surface.800}',
            filledFocusBackground: '{surface.800}',
            borderColor: '{surface.600}',
            hoverBorderColor: '{surface.500}',
            focusBorderColor: '{primary.color}',
            color: '{surface.0}',
          },
        },
      },
    },
    textarea: {
      colorScheme: {
        dark: {
          root: {
            background: '{surface.800}',
            filledBackground: '{surface.800}',
            borderColor: '{surface.600}',
            hoverBorderColor: '{surface.500}',
            focusBorderColor: '{primary.color}',
            color: '{surface.0}',
          },
        },
      },
    },
    datatable: {
      colorScheme: {
        dark: {
          row: {
            background: 'transparent',
            hoverBackground: '{surface.800}',
            selectedBackground: 'rgba(52, 211, 153, 0.12)',
            selectedHoverBackground: 'rgba(52, 211, 153, 0.18)',
            selectedColor: 'rgba(255, 255, 255, 0.87)',
            stripedBackground: 'rgba(255, 255, 255, 0.02)',
          },
          bodyCell: {
            selectedBorderColor: 'rgba(52, 211, 153, 0.2)',
          },
          headerCell: {
            background: '{surface.900}',
            hoverBackground: '{surface.800}',
            selectedBackground: 'rgba(52, 211, 153, 0.12)',
            borderColor: '{surface.700}',
            color: '{surface.0}',
            hoverColor: '{surface.0}',
            selectedColor: '{primary.color}',
          },
        },
      },
    },
    paginator: {
      colorScheme: {
        dark: {
          background: 'transparent',
          color: '{surface.400}',
        },
      },
    },
  },
})

export default NeoGoPreset
